/*
 * v1.8.0 — DHIS2 id → human-name resolver, mounted once per session.
 *
 * Many surfaces in the SPA render DHIS2 UIDs directly: TallyMetrics
 * buckets table, contributors drill-down, SyncedSourceData, Watermarks,
 * Stream console etc. Showing "Acme Health Centre" alongside "OUFa1c…"
 * costs the operator zero cognitive load; showing only "OUFa1c…" costs
 * a context switch out of the platform every time.
 *
 * The provider loads `/api/admin/labels` once at mount (per workspace)
 * and exposes hooks. Backend caches its workspace-scoped output so the
 * round-trip is cheap; the SPA holds it in memory for the session.
 *
 * Useful when a sync just landed: re-mount the provider via the
 * `refresh()` exposed on the context, or trust the metadata-version-bump
 * SSE event (future) to invalidate.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError } from "./api.js";

type LabelsPayload = Awaited<ReturnType<typeof api.labels>>;

interface LabelsContextValue {
  labels: LabelsPayload | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Lookup helpers that gracefully fall back to the id when the name is missing. */
  orgUnitName: (id: string | null | undefined) => string;
  programName: (id: string | null | undefined) => string;
  programStageName: (id: string | null | undefined) => string;
  dataElementName: (id: string | null | undefined) => string;
  dataSetName: (id: string | null | undefined) => string;
  categoryOptionComboName: (id: string | null | undefined) => string;
  attributeOptionComboName: (id: string | null | undefined) => string;
  trackedEntityAttributeName: (id: string | null | undefined) => string;
  optionName: (id: string | null | undefined) => string;
}

const LabelsContext = createContext<LabelsContextValue | null>(null);

export function LabelsProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<LabelsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.labels()
      .then((r) => setLabels(r))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo<LabelsContextValue>(() => {
    function lookup(map: Record<string, string> | undefined, id: string | null | undefined): string {
      if (!id) return "";
      return (map?.[id]) ?? id;
    }
    return {
      labels,
      loading,
      error,
      refresh: load,
      orgUnitName: (id) => lookup(labels?.orgUnits, id),
      programName: (id) => lookup(labels?.programs, id),
      programStageName: (id) => {
        if (!id) return "";
        return labels?.programStages?.[id]?.name ?? id;
      },
      dataElementName: (id) => lookup(labels?.dataElements, id),
      dataSetName: (id) => lookup(labels?.dataSets, id),
      categoryOptionComboName: (id) => lookup(labels?.categoryOptionCombos, id),
      attributeOptionComboName: (id) => lookup(labels?.attributeOptionCombos, id),
      trackedEntityAttributeName: (id) => lookup(labels?.trackedEntityAttributes, id),
      optionName: (id) => lookup(labels?.options, id)
    };
  }, [labels, loading, error, load]);

  return <LabelsContext.Provider value={value}>{children}</LabelsContext.Provider>;
}

/**
 * Hook that returns the entire LabelsContext. Most callers use one of
 * the focused helpers below to keep render code tight.
 */
export function useLabels(): LabelsContextValue {
  const ctx = useContext(LabelsContext);
  if (!ctx) {
    // Soft-degrade: if a page renders outside the provider, return
    // identity functions so it still shows the UID. Easier than
    // forcing every component to be wrapped in the provider during
    // tests / story-only rendering.
    return {
      labels: null, loading: false, error: null, refresh: () => {},
      orgUnitName: (id) => id ?? "",
      programName: (id) => id ?? "",
      programStageName: (id) => id ?? "",
      dataElementName: (id) => id ?? "",
      dataSetName: (id) => id ?? "",
      categoryOptionComboName: (id) => id ?? "",
      attributeOptionComboName: (id) => id ?? "",
      trackedEntityAttributeName: (id) => id ?? "",
      optionName: (id) => id ?? ""
    };
  }
  return ctx;
}

/**
 * Render helper for "name + uid" cells. Shows the human name as the
 * primary label and the UID as a small monospace subtext, so power-
 * users still have the id and casual users see the name. When the
 * label hasn't loaded yet (or the id isn't in the map), falls back to
 * the bare id so nothing is hidden.
 */
export function NamedId({ id, name }: { id: string | null | undefined; name: string }) {
  if (!id) return <span className="muted">—</span>;
  if (!name || name === id) {
    return <code style={{ fontSize: 11 }}>{id}</code>;
  }
  return (
    <span>
      <span>{name}</span>{" "}
      <code style={{ fontSize: 10, color: "var(--text-muted)" }}>{id}</code>
    </span>
  );
}
